import React, { FC, useEffect, useMemo, useState } from "react";
import { inject, observer } from "mobx-react";
import ExternalUserManagementStore from "./store";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { Card, CardBody, CardHeader, CardText } from "reactstrap";
import env from "../../../config/env";
import { MessageResultState } from "../../../types/general-types";
import { SSOExternalUserGridListDto } from "./types";
import { Button, Table } from "../../../components";
import CreateUpdateModal from "./components/create-update-modal";
import AssigningRoleToUserModal from "./components/assigning-role-to-user-modal";
import { BiPlus, BiTrash, BiPencil, BiUser } from "react-icons/bi";

interface IPage {
  ExternalUserManagementStore?: ExternalUserManagementStore;
}

const ExternalUserManagementPage: FC<IPage> = inject(
  "ExternalUserManagementStore"
)(
  observer((props: IPage) => {
    const { ExternalUserManagementStore: store } = props;
    const { t } = useTranslation();
    const { externalUsers } = store!;
    const [showModal, setShowModal] = useState(false);
    const toggleShowModal = () => setShowModal(!showModal);

    const [showAssigningRoleToUserModal, setAssigningRoleToUserModal] =
      useState(false);
    const toggleShowAssigningRoleToUserModal = () =>
      setAssigningRoleToUserModal(!showAssigningRoleToUserModal);

    const [selectedRow, setSelectedRow] =
      useState<SSOExternalUserGridListDto>();

    const columns = useMemo(
      () => [
        {
          Header: (
            <i style={{ color: "blue" }} className="fas fa-cog fa-2x"></i>
          ),
          id: "actions",
          Cell: ({ row }: any) => {
            row = Object.assign({}, row.original);
            const { personnelId } = row;
            return (
              <>
                <Button
                  className="btn btn-link text-success"
                  icon={<BiPencil style={{ marginRight: 3 }} />}
                  tooltipText={t("Update")}
                  onClick={async () => {
                    setSelectedRow(row);
                    setShowModal(true);
                  }}
                />
                <Button
                  className="btn btn-link text-danger"
                  icon={<BiTrash style={{ marginRight: 3 }} />}
                  tooltipText={t("Delete")}
                  onClick={async () => {
                    await store?.delete(row.id);
                  }}
                  showConfirm={true}
                />
                <Button
                  className="btn btn-link text-primary"
                  icon={<BiUser style={{ marginRight: 3 }} />}
                  tooltipText={t("Assigning a permission to the user")}
                  onClick={async () => {
                    setSelectedRow(row);
                    setAssigningRoleToUserModal(true);
                  }}
                />
              </>
            );
          },
          className: "d-flex justify-content-center text-center",
        },
        {
          Header: t("Name Surname"),
          id: "nameSurname",
          accessor: "nameSurname",
          Cell: ({ row }: any) => {
            return (
              <span
                dangerouslySetInnerHTML={{ __html: row.values.nameSurname }}
              />
            );
          },
          className: "d-none d-md-table-cell",
        },
        {
          Header: t("Email"),
          id: "email",
          accessor: "email",
          Cell: ({ row }: any) => {
            return (
              <span dangerouslySetInnerHTML={{ __html: row.values.email }} />
            );
          },
        },
        {
          Header: t("Phone Number"),
          id: "phoneNumber",
          accessor: "phoneNumber",
          Cell: ({ row }: any) => {
            return (
              <span
                dangerouslySetInnerHTML={{ __html: row.values.phoneNumber }}
              />
            );
          },
        },
        {
          Header: t("Username"),
          id: "username",
          accessor: "username",
          Cell: ({ row }: any) => {
            return (
              <span dangerouslySetInnerHTML={{ __html: row.values.username }} />
            );
          },
        },
      ],
      [t]
    );

    const page = useMemo(() => {
      return (
        <>
          <div className="card body overflow-hidden">
            <div className="row">
              <div className="">
                <Button
                  className="btn "
                  style={{
                    float: "right",
                    marginRight: 50,
                    marginTop: 20,
                    backgroundColor: "#23486A",
                    color: "#faf5f5",
                  }}
                  icon={<BiPlus style={{ marginRight: 3 }} />}
                  onClick={() => {
                    setSelectedRow(undefined);
                    toggleShowModal();
                  }}
                  // icon={<i className="fas fa-plus mr-2"></i>}
                  text={t("Add")}
                />
              </div>
            </div>
            <div className="p-3 p-lg-5">
              <Table
                columns={columns}
                smallSize={true}
                totalCount={externalUsers.totalCount}
                data={externalUsers.items}
                fetchData={store?.all}
                isLoading={!externalUsers.isLoaded}
              />
            </div>
          </div>
          {showModal && (
            <CreateUpdateModal
              isShow={showModal}
              toggleModal={toggleShowModal}
              selectedRow={selectedRow}
              t={t}
            />
          )}
          {showAssigningRoleToUserModal && selectedRow && (
            <AssigningRoleToUserModal
              isShow={showAssigningRoleToUserModal}
              toggleModal={toggleShowAssigningRoleToUserModal}
              selectedRow={selectedRow}
              t={t}
            />
          )}
        </>
      );
    }, [
      t,
      columns,
      externalUsers.totalCount,
      externalUsers.items,
      externalUsers.isLoaded,
      store?.all,
      showModal,
      toggleShowModal,
      selectedRow,
      showAssigningRoleToUserModal,
      toggleShowAssigningRoleToUserModal,
    ]);

    return <>{page}</>;
  })
);

export default ExternalUserManagementPage;
